import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "@/data/site";
import { WhatsAppButton } from "@/components/WhatsAppButton";

export const Wordmark = ({ light = false }) => (
    <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-2.5" aria-label="AXORA PS GLOBAL LLP — Home">
        <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white p-1.5">
            <img src="/images/logo.png" alt="AXORA PS GLOBAL LLP logo" className="h-full w-full object-contain" />
        </span>
        <span className="leading-tight">
            <span className={`block font-display text-base font-bold tracking-tight ${light ? "text-white" : "text-navy"}`}>
                AXORA PS GLOBAL
            </span>
            <span className={`block text-[11px] font-medium uppercase tracking-[0.22em] ${light ? "text-white/60" : "text-slate-500"}`}>
                Merchant Exporter LLP
            </span>
        </span>
    </Link>
);

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 border-b border-slate-200 bg-white/85 backdrop-blur-xl transition-shadow duration-300 ${
                scrolled ? "shadow-[0_8px_30px_rgba(10,37,64,0.08)]" : ""
            }`}
        >
            <div className="mx-auto flex h-[72px] w-full max-w-[1400px] items-center justify-between gap-4 px-5 sm:px-8 lg:px-10">
                <Wordmark />
                <nav className="hidden items-center gap-0.5 xl:flex" aria-label="Primary">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.path === "/products"}
                            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                            className={({ isActive }) =>
                                `whitespace-nowrap rounded-md px-2.5 py-2 text-[13px] font-medium transition-colors duration-200 ${
                                    isActive ? "bg-steel-light text-navy" : "text-slate-600 hover:bg-mist hover:text-navy"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="hidden xl:block">
                    <WhatsAppButton label="WhatsApp Quote" location="header" />
                </div>
                <button
                    type="button"
                    data-testid="mobile-menu-toggle"
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                    className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-navy xl:hidden"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>
            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-menu"
                        aria-label="Mobile"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-slate-200 bg-white xl:hidden"
                    >
                        <div className="space-y-1 px-5 py-5 sm:px-8">
                            {NAV.map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    end={item.path === "/products"}
                                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                                    className={({ isActive }) =>
                                        `block rounded-md px-4 py-3 text-[15px] font-medium transition-colors ${
                                            isActive ? "bg-steel-light text-navy" : "text-slate-600 hover:bg-mist hover:text-navy"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            <div className="pt-3">
                                <WhatsAppButton label="WhatsApp Quote" location="mobile-menu" className="w-full" />
                            </div>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
